import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoginInput, RegisterInput, UserAccount } from '../types';
import { mockBackend } from '../services/mockBackend';

type AuthContextValue = {
  user: UserAccount | null;
  loading: boolean;
  login: (payload: LoginInput) => Promise<UserAccount>;
  register: (payload: RegisterInput) => Promise<UserAccount>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = async () => {
    setLoading(true);
    try {
      const snapshot = await mockBackend.auth.getSession();
      setUser(snapshot.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  const login = async (payload: LoginInput) => {
    const snapshot = await mockBackend.auth.login(payload);
    if (!snapshot.user) {
      throw new Error('Sessão inválida após login.');
    }
    setUser(snapshot.user);
    return snapshot.user;
  };

  const register = async (payload: RegisterInput) => {
    const snapshot = await mockBackend.auth.register(payload);
    if (!snapshot.user) {
      throw new Error('Sessão inválida após cadastro.');
    }
    setUser(snapshot.user);
    return snapshot.user;
  };

  const logout = async () => {
    await mockBackend.auth.logout();
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      refreshSession,
    }),
    [loading, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider.');
  }

  return context;
};

export const RequireAuth = ({ children }: PropsWithChildren) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="container py-5">
        <div className="card bg-onyx border-0 shadow-lg">
          <div className="card-body py-5 text-center text-white-50">Carregando sessão...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/acesso" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export const RequireAdmin = ({ children }: PropsWithChildren) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="container py-5">
        <div className="card bg-onyx border-0 shadow-lg">
          <div className="card-body py-5 text-center text-white-50">Carregando sessão...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/acesso" replace state={{ from: location }} />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/portal" replace />;
  }

  return <>{children}</>;
};
